import {
  format,
  isSameDay,
  differenceInMinutes,
  addDays as dfAddDays,
  startOfISOWeek,
  isWeekend,
} from 'date-fns'

export const HOURS_PER_DAY = 8

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

export const dateKey  = (date) => format(date, 'yyyy-MM-dd')
export const cellId   = (rowId, key) => `${rowId}|${key}`

/** Monday-based week start. */
export function startOfWeek(date) {
  return startOfISOWeek(date)
}

export function addDays(date, n) {
  return dfAddDays(date, n)
}

export function buildDays(start, count) {
  const days = []
  for (let i = 0; i < count; i++) days.push(dfAddDays(start, i))
  return days
}

export function formatDayHeader(date) {
  return {
    weekday:   format(date, 'EEE'),
    dayMonth:  format(date, 'd MMM'),
    isWeekend: isWeekend(date),
    isToday:   isSameDay(date, new Date()),
  }
}

export function formatRange(start, end) {
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
}

export function formatFullDate(key) {
  const [y, m, d] = key.split('-').map(Number)
  return format(new Date(y, m - 1, d), 'EEEE, d MMM yyyy')
}

/**
 * Returns existing schedule for a person on a day.
 * scheduleMap: Map<"userId:yyyy-MM-dd", { status, bookedHours, capacityHours, project, projectId }>
 * Cells not in the map = free (no allocations for that day).
 */
const freeEntry = (capacityHours) => ({ status: 'free', bookedHours: 0, capacityHours, project: null, projectId: null })
const offEntry  = (capacityHours) => ({ status: 'off',  bookedHours: 0, capacityHours, project: null, projectId: null })

/** Account's configured standard day length (Letterhead total_hrs), falls back to HOURS_PER_DAY. */
export function getStandardHours(letterhead) {
  const parsed = parseFloat(letterhead?.total_hrs)
  if (!Number.isFinite(parsed) || parsed <= 0) return HOURS_PER_DAY
  return Math.round(parsed * 10) / 10
}

/** Set of weekday names from User.available_days.days; null when unset (caller defaults to all-available). */
export function availableDaysFromUserRecord(available_days) {
  const flags = available_days?.days
  if (!flags) return null
  const days = new Set()
  DAY_NAMES.forEach((day) => {
    if (flags[day]) days.add(day)
  })
  return days
}

/** True if staffId is available on date, per their Available Days set; defaults to true when unknown. */
export function isDayAvailable(availability, staffId, date) {
  const days = availability?.get(staffId)
  if (!days) return true
  return days.has(DAY_NAMES[date.getDay()])
}

/** Flattens a setting's per-date date_exceptions into Map<'yyyy-MM-dd', { name, type }>. */
export function exceptionDatesFromSetting(setting) {
  const map = new Map()
  ;(setting?.date_exceptions || []).forEach((item) => {
    if (item?.date && !map.has(item.date)) {
      map.set(item.date, { name: item.name || 'Exception', type: item.exception_type || null })
    }
  })
  return map
}

/** Exception info ({ name, type }) for staffId on a given day key, or null. */
export function getException(exceptionDates, staffId, key) {
  return exceptionDates?.get(staffId)?.get(key) || null
}

export function getDaySchedule(scheduleMap, staffId, key, availability, standardHours = HOURS_PER_DAY) {
  // A real booking always wins over "not scheduled to work" (e.g. a picked-up Saturday shift).
  const existing = scheduleMap.get(`${staffId}:${key}`)
  if (existing) return existing

  const [y, m, d] = key.split('-').map(Number)
  if (!isDayAvailable(availability, staffId, new Date(y, m - 1, d))) return offEntry(standardHours)
  return freeEntry(standardHours)
}

export function hasConflict(scheduleMap, staffId, key, availability, standardHours) {
  return getDaySchedule(scheduleMap, staffId, key, availability, standardHours).bookedHours > 0
}

/** Returns roster members already allocated to a specific project on a given day. */
export function getProjectDayStaff(scheduleMap, projectId, key, roster) {
  return roster.filter((s) => {
    const entry = scheduleMap.get(`${s.id}:${key}`)
    return entry && entry.projectId === projectId
  })
}

export const VIEW_DAYS = { week: 7, fortnight: 14, month: 28 }

/**
 * Transforms raw allocation API records into a Map keyed by "userId:yyyy-MM-dd".
 * Extracted here so it can be unit-tested independently of the page component.
 */
export function buildScheduleMap(allocations, standardHours = HOURS_PER_DAY) {
  const raw = new Map()

  ;(allocations || [])
    .filter((a) => a.status !== 'REJECTED')
    .forEach((a) => {
      if (!a.userId || !a.startTime) return
      // Bucket by the entry's explicit date if present, otherwise by startTime.
      // Either field is treated as naive UTC so we avoid local-timezone shifts.
      const day = (typeof a.date === 'string' && a.date.length >= 10 ? a.date.slice(0, 10) : null)
        || a.startTime.slice(0, 10)
      const key = `${a.userId}:${day}`
      const prev = raw.get(key) || {
        bookedHours:   0,
        capacityHours: standardHours,
        project:       null,
        projectId:     null,
        latestEndTime: null,
      }
      // Always derive hours from the real start/end time — never assume a flat standard day.
      const rawHours = Math.abs(differenceInMinutes(new Date(a.endTime), new Date(a.startTime))) / 60
      const hours = Math.round(rawHours * 10) / 10

      // Track the latest end time for the day so the scheduler can chain new entries after it.
      const latestEndTime = prev.latestEndTime && a.endTime
        ? (a.endTime > prev.latestEndTime ? a.endTime : prev.latestEndTime)
        : (a.endTime || prev.latestEndTime)

      raw.set(key, {
        bookedHours:   prev.bookedHours + hours, // not capped, so overlaps can be flagged as "over"
        capacityHours: standardHours,
        project:       prev.project   || (a.project?.name ?? null),
        projectId:     prev.projectId || (a.project?.id   ?? null),
        latestEndTime,
      })
    })

  const map = new Map()
  raw.forEach((v, k) => {
    map.set(k, {
      ...v,
      status: v.bookedHours > v.capacityHours  ? 'over'
            : v.bookedHours === v.capacityHours ? 'full'
            : v.bookedHours > 0                 ? 'partial'
            :                                      'free',
    })
  })
  return map
}
